/**
 * Shared helpers for the Espresso controlled vocabularies (phase, stage,
 * value_confidence, board_readiness, board_outcome).
 *
 * Mirrors `rag.ts`: each vocabulary gets a label function plus a Tailwind-class
 * function. Colour usage stays inside the BOI brand tokens — phase/stage pills
 * use blue-family shades (pale → electric → dark navy) with the RAG palette for
 * paused/blocked. Every coloured element also carries a text label, so colour is
 * never the only signal (brand rule).
 *
 * The generated API types expose these fields as plain nullable strings, so the
 * helpers accept `string | null | undefined` and fall back gracefully.
 */

// ─── Phase / Stage (shared vocabulary) ──────────────────────────────────────

export type Phase =
  | "not_started"
  | "discover"
  | "prioritize"
  | "prototype"
  | "board_prep"
  | "paused"
  | "blocked";

/** Human-readable label for a phase/stage value. */
export function phaseLabel(value: string | null | undefined): string {
  switch (value) {
    case "not_started":
      return "Not started";
    case "discover":
      return "Discover";
    case "prioritize":
      return "Prioritize";
    case "prototype":
      return "Prototype";
    case "board_prep":
      return "Board Prep";
    case "paused":
      return "Paused";
    case "blocked":
      return "Blocked";
    default:
      return value ? value : "—";
  }
}

/**
 * Tailwind classes for a phase pill (journey cells + weekly-pulse tags):
 * Discover = pale, Prioritize = mid, Prototype = electric blue, Board Prep =
 * dark navy, Paused = amber, Blocked = red.
 */
export function phasePillClass(value: string | null | undefined): string {
  const base = "inline-block rounded-sm px-2 py-0.5 text-center text-xs font-medium uppercase";
  switch (value) {
    case "discover":
      return `${base} bg-primary/10 text-primary`;
    case "prioritize":
      return `${base} bg-primary/25 text-primary`;
    case "prototype":
      return `${base} bg-primary text-primary-foreground`;
    case "board_prep":
      return `${base} bg-boi-dark text-white`;
    case "paused":
      return `${base} bg-rag-amber text-boi-dark-900`;
    case "blocked":
      return `${base} bg-rag-red text-boi-dark-900`;
    default:
      return `${base} bg-muted text-muted-foreground`;
  }
}

/** Tailwind classes for a small uppercase stage badge (same colour mapping). */
export function stageBadgeClass(value: string | null | undefined): string {
  return phasePillClass(value);
}

// ─── Value confidence ───────────────────────────────────────────────────────

/** Human-readable label for a value_confidence value. */
export function valueConfidenceLabel(value: string | null | undefined): string {
  switch (value) {
    case "high":
      return "High";
    case "medium":
      return "Medium";
    case "low":
      return "Low";
    case "tbc":
      return "TBC";
    default:
      return value ? value : "—";
  }
}

/** Text-colour class for value confidence: high=green, medium=amber, else muted. */
export function valueConfidenceClass(value: string | null | undefined): string {
  switch (value) {
    case "high":
      return "text-rag-green font-medium";
    case "medium":
      return "text-rag-amber font-medium";
    default:
      return "text-muted-foreground font-medium";
  }
}

// ─── Board readiness ────────────────────────────────────────────────────────

/** Human-readable label for a board_readiness value. */
export function boardReadinessLabel(value: string | null | undefined): string {
  switch (value) {
    case "ready":
      return "Ready";
    case "in_progress":
      return "In progress";
    case "early":
      return "Early";
    case "at_risk":
      return "At risk";
    case "blocked":
      return "Blocked";
    case "not_started":
      return "Not started";
    default:
      return value ? value : "—";
  }
}

/** Badge classes for board readiness. */
export function boardReadinessBadgeClass(value: string | null | undefined): string {
  const base = "inline-block rounded-sm px-2 py-0.5 text-xs font-medium uppercase";
  switch (value) {
    case "ready":
      return `${base} bg-primary text-primary-foreground`;
    case "in_progress":
      return `${base} bg-primary/25 text-primary`;
    case "early":
      return `${base} bg-primary/10 text-primary`;
    case "at_risk":
      return `${base} bg-rag-amber text-boi-dark-900`;
    case "blocked":
      return `${base} bg-rag-red text-boi-dark-900`;
    default:
      return `${base} bg-muted text-muted-foreground`;
  }
}

// ─── Board outcome (Aug-24 marker) ──────────────────────────────────────────

export interface BoardOutcomeMarker {
  emoji: string;
  label: string;
}

/** Emoji marker + label for a board_outcome value. */
export function boardOutcomeMarker(value: string | null | undefined): BoardOutcomeMarker | null {
  switch (value) {
    case "on_track":
      return { emoji: "✅", label: "On track" };
    case "monitor":
      return { emoji: "🔵", label: "Monitor" };
    case "at_risk":
      return { emoji: "⚠️", label: "At risk" };
    case "blocked":
      return { emoji: "🔴", label: "Blocked" };
    default:
      return null;
  }
}

// ─── Prototype RAG dot ──────────────────────────────────────────────────────

/** Background-colour class for the prototype status dot. */
export function prototypeDotClass(value: string | null | undefined): string {
  switch (value) {
    case "green":
      return "bg-rag-green";
    case "amber":
      return "bg-rag-amber";
    case "red":
      return "bg-rag-red";
    default:
      return "bg-muted-foreground/50";
  }
}
